import { useQuery } from "@tanstack/react-query";
import { pageContentSchema, type PageContent } from "@eaf/contracts";
import { ErrorState, LoadingState, PageFrame } from "@eaf/ui";
import { publicApi } from "../api/client";
import { Seo } from "../components/seo";

export function ContentPage({ slug }: { slug: string }) {
  const query = useQuery({
    queryKey: ["content", slug],
    queryFn: () => publicApi(`/content/pages/${slug}`, pageContentSchema)
  });
  if (query.isLoading)
    return (
      <PageFrame>
        <LoadingState label="Loading page" />
      </PageFrame>
    );
  if (query.isError || !query.data)
    return (
      <PageFrame>
        <ErrorState retry={() => void query.refetch()}>
          This page is temporarily unavailable.
        </ErrorState>
      </PageFrame>
    );
  const page = query.data;
  return (
    <PageFrame narrow>
      <Seo title={page.seo.title} description={page.seo.description} />
      <header className="page-header">
        <p className="eyebrow">Edmund Alan Fain Collection</p>
        <h1>{page.title}</h1>
        {page.subtitle ? <p className="lead">{page.subtitle}</p> : null}
      </header>
      <article className="content-document">
        {page.blocks.map((block, index) => (
          <ContentBlock key={`${block.type}-${index}`} block={block} />
        ))}
      </article>
    </PageFrame>
  );
}

function ContentBlock({ block }: { block: PageContent["blocks"][number] }) {
  switch (block.type) {
    case "heading":
      return block.level === 2 ? <h2>{block.text}</h2> : <h3>{block.text}</h3>;
    case "paragraph":
      return <p>{block.text}</p>;
    case "quote":
      return <blockquote>{block.text}</blockquote>;
    case "list":
      return (
        <ul>
          {block.items.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      );
    case "image":
      return (
        <figure>
          <img src={block.src} alt={block.alt} loading="lazy" />
        </figure>
      );
    case "callout":
      return (
        <aside className="content-callout">
          <h3>{block.title}</h3>
          {block.lines.map((line) => (
            <p key={line}>{line}</p>
          ))}
        </aside>
      );
  }
}
